import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {Environment} from 'compob2b-libs';
import {resolvePictureFallback} from './picture-fallback';

/**
 * Резервный домен для картинок в тестовых окружениях. Если картинка не загрузилась
 * с основного хоста (`environment.serverPicturesPath`, напр. next.compo-soft.ru),
 * делаем ОДНУ повторную попытку с резервного (`environment.serverPicturesPathFallback`,
 * напр. demo.compo-soft.ru). Включается только когда резервный хост задан — в prod он
 * пуст, поэтому сервис там no-op (и слушатель не вешается).
 *
 * Браузер-only: {@link register} зовётся из AppComponent через `afterNextRender` (нужен
 * реальный DOM). `error` на `<img>` НЕ всплывает → слушаем в capture-фазе на document,
 * один слушатель на все картинки страницы (без правки шаблонов). Повторную попытку метим
 * data-атрибутом, чтобы не зациклиться, если и резерв отдаёт ошибку.
 */
@Injectable({providedIn: 'root'})
export class ImageFallbackService {
  private readonly document = inject(DOCUMENT);
  private readonly environment = inject(Environment);
  private registered = false;

  register(): void {
    const primary = this.environment.serverPicturesPath;
    const fallback = this.environment.serverPicturesPathFallback;
    if (this.registered || !fallback) {
      return;
    }
    this.registered = true;

    this.document.addEventListener('error', (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== 'IMG') {
        return;
      }
      const img = target as HTMLImageElement;
      if (img.dataset['picFallbackTried']) {
        return;
      }
      const next = resolvePictureFallback(img.src, primary, fallback);
      if (next) {
        img.dataset['picFallbackTried'] = '1';
        img.src = next;
      }
    }, true);
  }
}
